import React from 'react';
import { Link } from 'react-router-dom';
import { Search, BarChart3, Zap, Shield, TrendingUp, Globe, CheckCircle, BookOpen, Target, Users, Award, Clock } from 'lucide-react';

const SEOGuidePage = () => {
  const seoTopics = [
    {
      icon: <Search className="w-6 h-6" />,
      title: 'On-Page SEO',
      description: 'Optimize your website content, titles, and meta tags for better search visibility',
      topics: ['Title tags', 'Meta descriptions', 'Header tags', 'Content optimization', 'Image alt text']
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: 'Technical SEO',
      description: 'Improve your website\'s technical foundation for search engines',
      topics: ['Site speed', 'Mobile optimization', 'Schema markup', 'XML sitemaps', 'Robots.txt']
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: 'User Experience',
      description: 'Create websites that users love and search engines reward',
      topics: ['Navigation', 'Page layout', 'Readability', 'Mobile-first design', 'Core Web Vitals']
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: 'Content Strategy',
      description: 'Develop content that ranks and drives organic traffic',
      topics: ['Keyword research', 'Content planning', 'Blog strategy', 'Internal linking', 'Content updates']
    }
  ];

  const aiSeoBenefits = [
    'Automated keyword research and analysis',
    'Predictive ranking insights',
    'Real-time algorithm monitoring',
    'Intelligent content recommendations',
    'Automated technical SEO audits',
    'Performance prediction models'
  ];

  const seoTools = [
    {
      name: 'oneclickAIseo',
      description: 'AI-powered SEO analysis tool',
      features: ['Free website analysis', 'Comprehensive reports', 'AI insights', 'Instant results']
    },
    {
      name: 'Google Search Console',
      description: 'Official Google SEO tool',
      features: ['Performance monitoring', 'Indexing status', 'Search queries', 'Mobile usability']
    },
    {
      name: 'Google Analytics',
      description: 'Website traffic analysis',
      features: ['Traffic sources', 'User behavior', 'Conversion tracking', 'Real-time data']
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Complete AI SEO Guide
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100 max-w-4xl mx-auto">
              Master search engine optimization with our comprehensive guide. Learn how AI is revolutionizing 
              SEO and discover proven strategies to improve your website's search rankings.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/"
                className="inline-flex items-center px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors duration-200"
              >
                <Search className="w-5 h-5 mr-2" />
                Start Free SEO Analysis
              </Link>
              <a
                href="#seo-basics"
                className="inline-flex items-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-blue-600 transition-colors duration-200"
              >
                <BookOpen className="w-5 h-5 mr-2" />
                Learn SEO Basics
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Basics Section */}
      <section id="seo-basics" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What is SEO and Why Does It Matter?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Search Engine Optimization (SEO) is the practice of optimizing your website to increase 
              its visibility in search engine results. With AI-powered tools, SEO has become more 
              sophisticated and effective than ever.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {seoTopics.map((topic, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 mb-4">
                  {topic.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {topic.title}
                </h3>
                <p className="text-gray-600 mb-4">
                  {topic.description}
                </p>
                <ul className="space-y-2">
                  {topic.topics.map((item, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI SEO Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                The Future of SEO: AI-Powered Optimization
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Artificial Intelligence is transforming how we approach SEO. AI tools can analyze 
                massive amounts of data, identify patterns, and provide insights that would take 
                humans months to discover.
              </p>
              
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">AI SEO Advantages:</h3>
                <div className="grid grid-cols-1 gap-3">
                  {aiSeoBenefits.map((benefit, index) => (
                    <div key={index} className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Why Choose AI SEO Tools?</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <Target className="w-6 h-6 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Precision Targeting</h4>
                    <p className="text-gray-600 text-sm">AI identifies the most effective keywords and strategies for your niche</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Zap className="w-6 h-6 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Real-time Optimization</h4>
                    <p className="text-gray-600 text-sm">Stay ahead of algorithm changes with instant updates and recommendations</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Award className="w-6 h-6 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Proven Results</h4>
                    <p className="text-gray-600 text-sm">Data-driven insights that consistently improve search rankings</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Tools Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Essential SEO Tools for Success
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover the best tools to analyze, optimize, and monitor your website's SEO performance. 
              From AI-powered analysis to comprehensive reporting, these tools will accelerate your SEO success.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {seoTools.map((tool, index) => (
              <div
                key={index}
                className={`bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow duration-300 ${
                  index === 0 ? 'ring-2 ring-blue-500' : ''
                }`}
              >
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 mb-4">
                  {index === 0 ? <Search className="w-6 h-6" /> : <BarChart3 className="w-6 h-6" />}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {tool.name}
                </h3>
                <p className="text-gray-600 mb-4">
                  {tool.description}
                </p>
                <ul className="space-y-2">
                  {tool.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                {index === 0 && (
                  <Link
                    to="/"
                    className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors duration-200"
                  >
                    Try Free
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Action Section */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Improve Your SEO?
          </h2>
          <p className="text-xl mb-8 text-blue-100 max-w-3xl mx-auto">
            Start with our free AI-powered SEO analysis tool. Get instant insights into your website's 
            performance and discover actionable recommendations to improve your search rankings.
          </p>
          <Link
            to="/"
            className="inline-flex items-center px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors duration-200"
          >
            <Search className="w-5 h-5 mr-2" />
            Start Free SEO Analysis
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SEOGuidePage;
